/**
 * accounts/registry — framework-agnostic multi-account registry.
 *
 * Holds N simultaneously-known accounts (real + guest) plus a pointer to the
 * "active" one, persisted as a single JSON blob under one storage key. The web
 * app uses Bearer tokens (one per account) so it can keep several sessions
 * primed at once — like the Gmail / Microsoft To Do account switcher.
 *
 * This module is PURE + storage-injected (same contract as `sessionMachine`
 * and the PowerSync connectors): callers pass a `TokenStorage`, so it works
 * identically in the browser (localStorage), Tauri and tests.
 *
 * The guest account is modelled explicitly: its `userId` is the shared
 * `VISITOR_ID` and its `kind` is `"guest"`. Upgrading a guest to a real account
 * is handled by the existing `sweepVisitorData` + merge dialog; the registry
 * just swaps the stored entry's identity.
 */

import { VISITOR_ID } from "../constants/common";
import type { TokenStorage } from "../powersync/base-connector";

/** Storage key holding the serialized {@link AccountsState}. */
export const ACCOUNTS_STORAGE_KEY = "stockpilot_accounts";

export type AccountKind = "real" | "guest";

/** A single known account. `token` is the per-account Bearer JWT (web). */
export interface StoredAccount {
  userId: string;
  email: string;
  displayName: string;
  avatar?: string;
  kind: AccountKind;
  /**
   * Per-account Bearer token. Empty/undefined for the guest account (it is
   * local-only) and may be refreshed lazily by the connector.
   *
   * Phase 3 (Tauri DESKTOP): this is NO LONGER persisted to plaintext
   * `localStorage` — the durable copy lives in the OS keychain keyed by
   * `userId` (see `apps/web/src/lib/secure-token.ts`). It is re-hydrated into
   * this transient field on read so the synchronous consumers (the API
   * client's `getAuthToken`, the role/identity view) are unchanged. On web it
   * stays `undefined` (cookie-BFF) and on mobile it is still persisted inline
   * (keychain follow-up). See {@link hasToken}.
   */
  token?: string;
  /**
   * Durable marker that a Bearer token EXISTS for this account in the secure
   * store, persisted in place of the raw {@link token} on Tauri desktop. Lets
   * the switcher/boot logic know an account is authenticated without exposing
   * the secret in `localStorage`.
   */
  hasToken?: boolean;
  /**
   * The account's role (e.g. `user`/`admin`), persisted from login + every
   * `get-session` reflection. Surfaced by the per-tab session view so the
   * Profile's Role badge stays correct even when the LIVE session is
   * unavailable (expired/cleared by a 401, or the minimal session primed right
   * after an in-app switch) — mirroring how `email`/`displayName` already
   * persist here. UX-only: the server still enforces every role-gated action.
   */
  role?: string;
}

export interface AccountsState {
  accounts: StoredAccount[];
  /** `userId` of the active account, or `null` when none is selected. */
  activeUserId: string | null;
}

/** The canonical empty state. */
export function emptyAccountsState(): AccountsState {
  return { accounts: [], activeUserId: null };
}

// ─── Pure reducers (no storage) ────────────────────────────────────────────

/**
 * Adds or updates an account (matched by `userId`) and makes it active. When
 * the account already exists its mutable fields are merged so a re-login
 * refreshes the token/profile without losing the existing entry.
 */
export function upsertAccount(state: AccountsState, account: StoredAccount): AccountsState {
  const index = state.accounts.findIndex((a) => a.userId === account.userId);
  const accounts =
    index === -1
      ? [...state.accounts, account]
      : state.accounts.map((a, i) => {
          if (i !== index) {
            return a;
          }
          // Merge, but never clobber a stored token/role with `undefined` — a
          // profile refresh (e.g. via get-session) may carry no token, yet we
          // must keep the existing Bearer so switching away and back still
          // authenticates, and keep the last-known role for the Profile badge.
          return {
            ...a,
            ...account,
            token: account.token ?? a.token,
            role: account.role ?? a.role,
          };
        });
  return { accounts, activeUserId: account.userId };
}

/**
 * Adds or refreshes an account's profile WITHOUT changing the active pointer.
 *
 * Used by the passive `get-session` reflection: the backend cookie may still
 * authenticate a real account even after the user switched to the guest (or to
 * another account), so a `get-session` reply must NOT silently re-activate that
 * account — otherwise "Continue as guest" / an account switch is undone the
 * moment the background session query lands. We still register/refresh the
 * account so it appears in the switcher, but the user's chosen active account is
 * preserved. The only exception: if there is no active account yet, we adopt
 * this one (first sign-in path).
 */
export function syncAccountProfile(state: AccountsState, account: StoredAccount): AccountsState {
  const index = state.accounts.findIndex((a) => a.userId === account.userId);
  const accounts =
    index === -1
      ? [...state.accounts, account]
      : state.accounts.map((a, i) =>
          i === index
            ? { ...a, ...account, token: account.token ?? a.token, role: account.role ?? a.role }
            : a,
        );
  const activeUserId = state.activeUserId ?? account.userId;
  return { accounts, activeUserId };
}

/** Stores/updates only the token of an existing account (no-op if unknown). */
export function setAccountToken(
  state: AccountsState,
  userId: string,
  token: string | null,
): AccountsState {
  return {
    ...state,
    accounts: state.accounts.map((a) =>
      a.userId === userId ? { ...a, token: token ?? undefined } : a,
    ),
  };
}

/**
 * Removes an account. If the removed account was active, the active pointer
 * falls back to the first remaining account (or `null`).
 */
export function removeAccount(state: AccountsState, userId: string): AccountsState {
  const accounts = state.accounts.filter((a) => a.userId !== userId);
  let { activeUserId } = state;
  if (activeUserId === userId) {
    activeUserId = accounts[0]?.userId ?? null;
  }
  return { accounts, activeUserId };
}

/** Sets the active account. Ignored (returns the same state) if unknown. */
export function setActiveAccount(state: AccountsState, userId: string): AccountsState {
  if (!state.accounts.some((a) => a.userId === userId)) {
    return state;
  }
  return { ...state, activeUserId: userId };
}

/** Returns the active account, or `null`. */
export function getActiveAccount(state: AccountsState): StoredAccount | null {
  if (!state.activeUserId) {
    return null;
  }
  return state.accounts.find((a) => a.userId === state.activeUserId) ?? null;
}

/** Returns the existing guest account if present. */
export function getGuestAccount(state: AccountsState): StoredAccount | null {
  return state.accounts.find((a) => a.kind === "guest") ?? null;
}

/** Builds the canonical guest account entry. */
export function makeGuestAccount(displayName = "Guest"): StoredAccount {
  return { userId: VISITOR_ID, email: "", displayName, kind: "guest" };
}

// ─── Validation / (de)serialization ─────────────────────────────────────────

function isStoredAccount(value: unknown): value is StoredAccount {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (
    "userId" in value &&
    typeof value.userId === "string" &&
    "email" in value &&
    typeof value.email === "string" &&
    "displayName" in value &&
    typeof value.displayName === "string" &&
    "kind" in value &&
    (value.kind === "real" || value.kind === "guest")
  );
}

/** Parses persisted JSON defensively, dropping malformed entries. */
export function parseAccountsState(raw: string | null): AccountsState {
  if (!raw) {
    return emptyAccountsState();
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return emptyAccountsState();
    }
    const accounts = "accounts" in parsed ? parsed.accounts : undefined;
    const rawActive = "activeUserId" in parsed ? parsed.activeUserId : undefined;
    const validAccounts = Array.isArray(accounts) ? accounts.filter(isStoredAccount) : [];
    const active =
      typeof rawActive === "string" && validAccounts.some((a) => a.userId === rawActive)
        ? rawActive
        : (validAccounts[0]?.userId ?? null);
    return { accounts: validAccounts, activeUserId: active };
  } catch {
    return emptyAccountsState();
  }
}

// ─── Storage-backed store ────────────────────────────────────────────────────

/**
 * Thin async store around an injected {@link TokenStorage}. All mutations read
 * the current state, apply a pure reducer and persist the result, returning the
 * new state so callers can react without a second read.
 */
export class AccountsStore {
  private readonly storage: TokenStorage;

  /**
   * Serializes every read-modify-write so they can never interleave.
   *
   * BUG (Bearer-null after login, the 401 storm): `mutate` reads the state,
   * applies a reducer and writes it back — but `load()` and `save()` each await,
   * so two concurrent `mutate` calls used to BOTH read the SAME pre-state and
   * then write back in turn, last-writer-wins clobbering the other's change
   * (a classic lost update). At login two writers run around the same time:
   *
   *   1. `registerAccountFromResponse` → `upsert(realAccount WITH its Bearer)`,
   *   2. the `useSession` get-session reflection → `syncProfile(real profile
   *      WITHOUT a token)` (a fire-and-forget `void`).
   *
   * If (2) read the registry BEFORE (1) had written the token and then saved
   * last, it recreated the real account from a token-less snapshot — leaving it
   * with email/role but NO Bearer. `getActiveAuthToken()` then returns `null`,
   * the `Authorization` header is dropped and EVERY authenticated request 401s.
   * `syncProfile`'s `token: account.token ?? a.token` merge only preserves the
   * Bearer if it actually reads the post-`upsert` state — which requires the
   * mutations to be ATOMIC. Chaining them on this queue guarantees exactly that,
   * so the token-less reflection always observes (and preserves) the token.
   */
  private queue: Promise<unknown> = Promise.resolve();

  constructor(storage: TokenStorage) {
    this.storage = storage;
  }

  async load(): Promise<AccountsState> {
    const raw = await this.storage.getItem(ACCOUNTS_STORAGE_KEY);
    return parseAccountsState(raw);
  }

  private async save(state: AccountsState): Promise<AccountsState> {
    await this.storage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(state));
    return state;
  }

  private async mutate(fn: (state: AccountsState) => AccountsState): Promise<AccountsState> {
    // Append this read-modify-write to the serial queue: it only starts once the
    // PREVIOUS mutation has fully persisted, so every reducer reads the latest
    // committed state (no lost updates). The queue advances regardless of whether
    // a prior op resolved or rejected, so one failure can't wedge the chain.
    const run = this.queue.then(async () => {
      const current = await this.load();
      return this.save(fn(current));
    });
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async upsert(account: StoredAccount): Promise<AccountsState> {
    return this.mutate((s) => upsertAccount(s, account));
  }

  /** Adds/refreshes an account WITHOUT stealing the active pointer. */
  async syncProfile(account: StoredAccount): Promise<AccountsState> {
    return this.mutate((s) => syncAccountProfile(s, account));
  }

  async setToken(userId: string, token: string | null): Promise<AccountsState> {
    return this.mutate((s) => setAccountToken(s, userId, token));
  }

  async remove(userId: string): Promise<AccountsState> {
    return this.mutate((s) => removeAccount(s, userId));
  }

  async setActive(userId: string): Promise<AccountsState> {
    return this.mutate((s) => setActiveAccount(s, userId));
  }

  /** Ensures a guest account exists and makes it active. */
  async ensureGuestActive(displayName?: string): Promise<AccountsState> {
    return this.mutate((s) => {
      const existing = getGuestAccount(s);
      if (existing) {
        return setActiveAccount(s, existing.userId);
      }
      return upsertAccount(s, makeGuestAccount(displayName));
    });
  }

  async getActive(): Promise<StoredAccount | null> {
    return getActiveAccount(await this.load());
  }

  async getActiveToken(): Promise<string | null> {
    const active = await this.getActive();
    return active?.token ?? null;
  }

  /** Clears the entire registry (e.g. a full reset). */
  async clear(): Promise<void> {
    await this.storage.setItem(ACCOUNTS_STORAGE_KEY, "");
  }
}
